import { useState, useContext, useEffect } from "react";
import "./Login.css";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { Toast } from "../../Common/Toast-Snackbar/ToastSnackbar";
import DynamicTextField from "../../Common/Textfield/Textfield";
import Button from "../../Common/Button/Button";
import vybogTalLogo from "../../Assets/Images/VybogTAL-Logo.svg";
import vybogTALDarkLogo from "../../Assets/Images/VybogTAL-Logo-Dark.svg";
import SignUpIcon from "../../Assets/Images/Sign_Up_Icon.svg";
import { DynamicContext } from "../../Context/DynamicContext";
import { post, get } from "../../helper/Api_helper";
import {
  LOGIN_URL,
  VYLABS_BASE_URL,
  GET_RECRUITER_BY_ID,
} from "../../helper/url_helper";
import { scheduleTokenCheck } from "../../helper/helper_functions";
import Cookies from "js-cookie";
import StackedCardCarousel from "../StackedCardCarousel/StackedCardCarousel";
import {
  TENANT_OTP_API_KEY,
} from "../../Tenant-Config/TenantConfig";

function LoginPage() {
  const {
    setUserDetails,
    isDarkMode,
      setSubscriptionRefetchKey,
  } = useContext(DynamicContext);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);

  const formik = useFormik({
    initialValues: {
      LoginUsername: "",
      LoginPassword: "",
      SignupCandidateName: "",
      SignupEmail: "",
      SignupPassword: "",
    },
    validationSchema: Yup.object({
      LoginUsername: Yup.string().required(
        "Please enter your email.",
      ),
      LoginPassword: Yup.string().required("Please enter your password."),
      SignupCandidateName: Yup.string()
        .matches(/^[A-Za-z ]*$/, "Only letters and spaces are allowed")
        .required("Please Enter your Fullname"),
      SignupEmail: Yup.string()
        .email("Invalid email address")
        .required("Please Enter your Email"),
      SignupPassword: Yup.string()
        .required("Please Enter your password")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
          "Must Contain 8 Characters, One Uppercase, One Lowercase, One Number and One Special Case Character",
        )
        .max(15, "Password must not exceed 15 characters"),
    }),
  });

  const GetLoginValue = async () => {
    formik.handleSubmit();
    const { LoginUsername, LoginPassword } = formik.values;

    if (!LoginUsername || !LoginPassword) {
      if (!LoginUsername) Toast("error", "Email is required");
      if (!LoginPassword) Toast("error", "Password is required");
      return;
    }

    setIsLoading(true);
    try {
      localStorage.removeItem("sessionExpired");
      const response = await post(VYLABS_BASE_URL, LOGIN_URL, {
        email: formik.values.LoginUsername,
        password: formik.values.LoginPassword,
      },
      {
        _skipAuth: true,
        headers: {
          "X-API-Key": TENANT_OTP_API_KEY,
        },
      },);
      const {
        access_token,
        refresh_token,
        access_token_expires_at,
        refresh_token_expires_at,
        session_id,
        user,
      } = response || {};
      localStorage.setItem("token", access_token);
      localStorage.setItem("tokenExpiry", access_token_expires_at);
      localStorage.setItem("sessionId", session_id);
      const refreshTokenExpiry = new Date(refresh_token_expires_at);
      Cookies.set("refreshToken", refresh_token, {
        expires: refreshTokenExpiry,
        secure: true,
        sameSite: "strict",
      });
      Cookies.set("refreshTokenExpiry", refresh_token_expires_at, {
        expires: refreshTokenExpiry,
        secure: true,
        sameSite: "strict",
      });
      const recruiterDetailsResponse = await get(
        VYLABS_BASE_URL,
        `${GET_RECRUITER_BY_ID}?id=${user.id}`,
      );
      localStorage.setItem("user", JSON.stringify(recruiterDetailsResponse));
      setUserDetails(recruiterDetailsResponse?.user);
      setSubscriptionRefetchKey((k) => k + 1);
      Toast("success", "Login Successfully");
      navigate("/dashboard", { state: { userData: user } });
      scheduleTokenCheck(access_token_expires_at);
    } catch (error) {
      console.error("Login error:", error);

      if (error.response) {
        const status = error.response.status;
        const responseData = error.response?.data;
        const message =
          responseData?.message ||
          responseData?.error ||
          (typeof responseData === "string"
            ? responseData
            : "Something went wrong");

        if (message) {
          Toast("error", message);
        } else if (status === 400) {
          Toast("error", message || "Invalid input");
        } else if (status === 401) {
          Toast("error", message || "Unauthorized access");
        } else if (status === 500) {
          Toast("error", message || "Server error. Please try again later.");
        } else {
          Toast("error", message);
        }
      } else if (error.request) {
        Toast("error", "No response from server");
      } else {
        Toast("error", error.message || "Unexpected error occurred");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignupPage = () => {
    navigate("/signup");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      GetLoginValue();
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth > 768);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <div className="login-page">
      <div className="login-main-container">

        <div className="login-container" onKeyDown={handleKeyDown}>
          <div className="login-header">
            <img alt="vylogo" src={isDarkMode ? vybogTALDarkLogo : vybogTalLogo} className="vylogo" />
          </div>
          <div className="login-header-text-container">
            <h1 style={{ margin: 0 }}>Login</h1>
            <h4>Welcome to VybogTAL, our Resume Lookbook</h4>
          </div>
          <div className="input-container">
            <DynamicTextField
              type="text"
              placeholder="Enter your email"
              label={"Email"}
              labelStyle={{ fontWeight: 500 }}
              name="LoginUsername"
              value={formik.values.LoginUsername}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.LoginUsername && formik.errors.LoginUsername
              }
              errorMessage={formik.errors.LoginUsername}
              required={true}
            />
            <div className="password-div">
              <DynamicTextField
                type={"password"}
                placeholder="Enter your password"
                label={"Password"}
                labelStyle={{ fontWeight: 500 }}
                name="LoginPassword"
                value={formik.values.LoginPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.LoginPassword && formik.errors.LoginPassword
                }
                errorMessage={formik.errors.LoginPassword}
                showPasswordToggle={true}
                required={true}
              />
            </div>
            <div
              style={{
                textAlign: "right",
                marginTop: "-8px",
                fontSize: "14px",
              }}
            >
              <span
                className="signup-text-span"
                style={{ color: "#6866ee", cursor: "pointer" }}
                onClick={() => navigate("/forgot-password")}
              >
                Forgot Password?
              </span>
            </div>
          </div>

          <div className="login-options">
            <Button
              event={GetLoginValue}
              backgroundColor="#6860EE"
              width="70%"
              padding="12px 14px"
              label={isLoading ? "Initiating Access to VybogTAL..." : "Login"}
              disabled={isLoading}
            />
          </div>
          <div className="signup-text">
            New to VybogTAL?{" "}
            <span
              className="signup-text-span"
              onClick={handleSignupPage}
              style={{ color: "#6866ee" }}
            >
              Sign Up
            </span>
            <span className="signup-text-span">
              <img
                onClick={handleSignupPage}
                alt="anchor Icon"
                src={SignUpIcon}
                style={{ width: "20px", height: "20px", marginTop: "2px" }}
              />
            </span>
          </div>
        </div>
      </div>
      {isDesktop && (
        <div className="stacked-carousel-panel">
          <StackedCardCarousel />
        </div>
      )}
    </div>
  );
}

export default LoginPage;
